import { useLazyQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Form, Formik } from "formik";
import { yupRules } from "@portal/app/_common/lib/yupValidationRules";
import { FormFieldError } from "@portal/app/_components/ui-elements/Form";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { FormInputText } from "@portal/app/_components/ui-elements/FormInputs";
import { object } from "yup";
import { gql } from "graphql-tag";
import { VerifyAddressForDreamSearchViewerComponentDocument } from "./generated";
import { COOKIE_KEYS, setCookie } from "@portal/app/_common/lib/headers";
import { ReadonlyURLSearchParams, useRouter, useSearchParams } from "next/navigation";
import { getUrls } from "@portal/app/_common/lib/urls";
import { cleanMaxLengthNumber } from "@portal/app/_common/lib/formatRenderers";
import { DREAM_SEARCH_LOCATION_PROMPT_PARAM } from "@portal/app/_common/lib/dreamSearchRoutes";

void gql`
  query verifyAddressForDreamSearchViewerComponent($zip: String!) {
    verifyZip(zip: $zip)
  }
`;

export const ZipCodeForm = ({
  industry,
  setIsEditingZipCode,
  canCancel = true,
  shouldPromptForLocation = false,
}: {
  industry?: GQL.VocationIndustry;
  setIsEditingZipCode: (isEditingZipCode: boolean) => void;
  canCancel?: boolean;
  shouldPromptForLocation?: boolean;
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [verifyZipCode] = useLazyQuery(VerifyAddressForDreamSearchViewerComponentDocument, {
    notifyOnNetworkStatusChange: true,
  });

  // This handler is needed to properly wipe out the map in the same render as the zip code is unset.
  const updateZipCode = async (newZipCode: string | undefined) => {
    if (newZipCode) {
      setCookie(COOKIE_KEYS.DREAMSEARCH_SESSION_ZIP, newZipCode);
    }

    const destinationParams = new URLSearchParams(searchParams.toString());
    destinationParams.delete(DREAM_SEARCH_LOCATION_PROMPT_PARAM);
    destinationParams.delete("zip");

    const destination = getUrls().DreamSearch({
      ...(industry && { industry }),
      zip: newZipCode,
      filterQueryParams: new ReadonlyURLSearchParams(destinationParams),
    });
    // Replace the synthetic landing so Back cannot restore its cached mandatory prompt.
    if (shouldPromptForLocation) {
      router.replace(destination);
    } else {
      router.push(destination);
    }
  };

  const yupZipForm = object({
    zipCode: yupRules.required.zip.test(
      "verify-zip-code",
      "A valid ZIP code is required",
      async (zip) => {
        if (!zip) {
          return false;
        }
        const res = await verifyZipCode({
          variables: { zip },
        }).catch(() => undefined);

        return !!res?.data?.verifyZip;
      }
    ),
  });

  return (
    <Box sx={{ display: "inline-block" }}>
      <Formik
        initialValues={{ zipCode: "" }}
        onSubmit={async (values) => {
          await updateZipCode(values.zipCode);
          setIsEditingZipCode(false);
        }}
        validationSchema={yupZipForm}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Stack
              spacing={1}
              direction="row"
              sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
            >
              <Stack spacing={1} direction="row" sx={{ maxWidth: "350px", minHeight: "3rem" }}>
                <ValidatedFormikField
                  as={FormInputText}
                  $fromDisplay={cleanMaxLengthNumber(5)}
                  $toDisplay={cleanMaxLengthNumber(5)}
                  name="zipCode"
                  style={{ flex: 1, height: "3rem" }}
                  maxLength={5}
                  autoComplete="postal-code"
                  autoFocus
                  placeholder={"_ _ _ _ _"}
                  data-testid="zip-code-input"
                />
                <Button type="submit" data-testid="find-classes-by-zip">
                  Find classes
                </Button>
                {canCancel && (
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => setIsEditingZipCode(false)}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
              <FormFieldError name="zipCode" />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
